import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@root/rootReducer';
import { useNavigate } from 'react-router-dom';
import './LoginPage.scss';

import { ReactComponent as Close } from 'img/btn-close.svg';
import Button from '@components/Button';
import Input from '@components/Input';
import useAuth from '@hooks/Auth/useAuth';
import LogoAndTitle from '@components/Logo/Logo';
import { configStore } from 'config/configStore';
import { getVersion } from '@api/version';

const LoginPage: React.FC = (): React.JSX.Element => {
  const env = useSelector((state: RootState) => state.env.isElectron);
  const navigate = useNavigate();
  const [inputUserId, setInputUserId] = useState<string>('');
  const [inputUserPw, setInputUserPw] = useState<string>('');
  const [IsError, setIsError] = useState<boolean>(false);
  const [showPass, setShowPass] = useState<boolean>(false);
  const [idEntered, setIdEntered] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const passwordRef = React.useRef<HTMLInputElement>(null);

  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));
  const { login } = useAuth();
  const [version, setversion] = useState<string>('');

  useEffect(() => {
    (async () => {
      const [status, version_info] = await getVersion();

      if (status && version_info) {
        setversion(String(version_info['helmChart']));
      } else {
        console.warn('version status: ', status);
        console.warn('version version_info: ', version_info);
        setversion('');
      }
    })();
  }, []);

  useEffect(() => {
    const autologin = async () => {
      if (configStore.get().autoLogin) {
        try {
          await login({
            userName: 'admin',
            password: 'password',
          }).then(() => navigate('/browser'));
        } catch (error: any) {
          setIsError(true);
          setErrorMessage(error?.message || 'Auto Login Failed');
        }
        return;
      }
    };
    autologin();
  }, [true]);

  const handleClose = async () => {
    if (process.env.NODE_ENV !== 'production') {
      try {
        await login({
          userName: 'admin',
          password: 'password',
        }).then(() => navigate('/browser'));
      } catch (error: any) {
        setIsError(true);
      }
      return;
    }
    if (env) {
      window.electron.ipcRenderer.sendMessage('window-handle', ['close']);
    }
  };

  // const handleCloseTemp = async () => {
  //   try {
  //     await login({
  //       userName: 'admin',
  //       password: 'password',
  //     })
  //     // await delay(2000);
  //     navigate('/browser');
  //   } catch (error: any) {
  //     setIsError(true);
  //   }
  // };

  const handleNext = () => {
    setIdEntered(true);
    setTimeout(() => {
      passwordRef.current?.focus();
    }, 0);
  };

  const handleLogin = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await login({
        userName: inputUserId,
        password: inputUserPw,
      }).then(() => navigate('/browser'));
    } catch (error: any) {
      setIsError(true);
      setErrorMessage(
        error?.message || 'Invalid ID or password. Please try again.',
      );
      setInputUserPw('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setInputUserId('');
    setInputUserPw('');
    setIsError(false);
    setIdEntered((prev) => !prev);
  };

  return (
    <div className="login-page">
      <div className="login-page-container">
        <div className="login-page-container-header">
          <div className="login-page-container-header-frame">
            <LogoAndTitle goList={false} className="login-logo-static" />
            <span className="login-page-container-header-version">
              {version}
            </span>
            <Close
              className="login-page-container-header-close"
              onClick={handleClose}
            />
            {/* {process.env.NODE_ENV !== 'production' && (
              <Close
                className="login-page-container-header-close"
                onClick={handleClose}
              />
            )} */}
          </div>
        </div>
        <div className="login-page-container-middle">
          <div className="login-page-container-middle-item">
            <div className="login-page-container-middle-item-label">
              <span className="label">{`ID`}</span>
            </div>
            <Input
              className="login-page-input"
              type="id"
              value={inputUserId}
              onChange={(e) => setInputUserId(e.target.value)}
              error={IsError}
              placeholder="ID"
              disabled={false}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !idEntered) {
                  handleNext();
                }
              }}
            />
          </div>
          <div className="login-page-container-middle-item">
            <div className="login-page-container-middle-item-label">
              <span className="label">{`Password`}</span>
            </div>
            <Input
              className="login-page-input"
              type="password"
              value={inputUserPw}
              onChange={(e) => setInputUserPw(e.target.value)}
              placeholder="Password"
              error={IsError}
              showPassword={showPass}
              onTogglePassword={() => setShowPass((prev) => !prev)}
              disabled={false}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && inputUserPw.length >= 8) {
                  handleLogin();
                }
              }}
              ref={passwordRef}
            />
            {IsError && errorMessage && (
              <span className="login-page-container-middle-error">
                {!idEntered
                  ? 'Please enter your ID'
                  : idEntered && !IsError
                    ? 'Please enter your password'
                    : IsError && errorMessage
                      ? errorMessage
                      : null}
              </span>
            )}
          </div>
        </div>
        <div className="login-page-container-bottom">
          <Button
            className={`login-button${isLoading ? ' loading' : ''}`}
            onClick={handleLogin}
            // disabled={inputUserPw.length < 8 || isLoading}
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </Button>
        </div>
        {/* {!idEntered ? (
          <Button className="button-size-login" onClick={handleNext}>
            Next
          </Button>
        ) : (
          <div style={{ display: 'flex', gap: 4 }}>
            <Button
              className={`button-size-login${isLoading ? ' loading' : ''}`}
              onClick={handleLogin}
              disabled={inputUserPw.length < 8 || isLoading}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
            <Button
              className={`button-size-login2${isLoading ? ' loading' : ''}`}
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default LoginPage;
